/**
 * Shared request contract for click-to-call originate:
 *   POST /api/calls/originate
 *   POST /api/v2/voip/active-calls/originate
 *
 * Content rules (ESL command sanitisation): every field that ends up inside
 * the FreeSWITCH `originate` string is shape-checked here, at the boundary,
 * with the same patterns the backend branded helpers enforce. The SIP domain
 * is NOT part of the contract: the backend derives it from the authenticated
 * tenant (`tenants.sip_domain`). A legacy `domain` field is still tolerated
 * for old clients but the route rejects it when it differs from the tenant's.
 */
import { z } from 'zod';

/** Internal extension: digits, `*`, `#`, 2..10 chars. */
export const ESL_EXTENSION_PATTERN = /^[0-9*#]{2,10}$/;
/** Dialable target: optional leading `+`, digits/`*`/`#`, 2..20 chars. */
export const ESL_DIAL_TARGET_PATTERN = /^\+?[0-9*#]{2,20}$/;
/**
 * Caller-ID (name or number): no `,` `{` `}` `'` `"` CR/LF nor control
 * characters, 1..80 chars. Spaces are allowed (quoted by the builder).
 */
// eslint-disable-next-line no-control-regex -- i caratteri di controllo (\x00-\x1f, \x7f) vanno esclusi esplicitamente dagli argomenti ESL
export const ESL_CALLER_ID_PATTERN = /^(?!\s)(?!.*\s$)[^,{}'"\r\n\x00-\x1f\x7f]{1,80}$/;
/** Dialplan context label recorded on the CDR (never interpolated raw). */
export const ESL_CONTEXT_LABEL_PATTERN = /^[A-Za-z0-9._-]{1,64}$/;

const CallerIdSchema = z
  .string()
  .regex(ESL_CALLER_ID_PATTERN, 'Invalid caller id');

const RawOptionsSchema = z
  .object({
    timeout: z.number().int().min(1).max(300).optional(),
    /** Canonical name. */
    caller_id: CallerIdSchema.optional(),
    /** Legacy camelCase alias — normalised to `caller_id`. */
    callerId: CallerIdSchema.optional(),
    context: z.string().regex(ESL_CONTEXT_LABEL_PATTERN, 'Invalid context').optional(),
    recording: z.boolean().optional(),
  })
  .strict();

export const OriginateCallOptionsSchema = RawOptionsSchema.transform((raw, ctx) => {
  if (raw.caller_id !== undefined && raw.callerId !== undefined && raw.caller_id !== raw.callerId) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['callerId'],
      message: 'caller_id and callerId disagree; send only caller_id',
    });
    return z.NEVER;
  }
  const { callerId, ...rest } = raw;
  return {
    ...rest,
    ...(rest.caller_id === undefined && callerId !== undefined ? { caller_id: callerId } : {}),
  };
});
export type OriginateCallOptions = z.infer<typeof OriginateCallOptionsSchema>;

export const OriginateCallRequestSchema = z
  .object({
    caller_extension: z
      .string()
      .regex(ESL_EXTENSION_PATTERN, 'Invalid caller extension'),
    callee_number: z
      .string()
      .regex(ESL_DIAL_TARGET_PATTERN, 'Invalid callee number'),
    /**
     * Deprecated: ignored for routing. Kept optional so legacy clients keep
     * validating; the route answers 400 `ORIGINATE_DOMAIN_MISMATCH` when it
     * is present and differs from the tenant's `sip_domain`.
     */
    domain: z.string().min(1).max(253).optional(),
    campaign_id: z.string().uuid('Invalid campaign UUID').optional().nullable(),
    options: OriginateCallOptionsSchema.optional(),
  })
  .strict();
export type OriginateCallRequest = z.infer<typeof OriginateCallRequestSchema>;
export type OriginateCallRequestInput = z.input<typeof OriginateCallRequestSchema>;
